import {
  useState, useEffect, useMemo, useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import { setKeyValue, updateKeyValue } from 'modules/keyValueStore/actions';
import { useKeyValueStore } from 'modules/keyValueStore/useKeyValueStore';
import { FAILURE, PENDING, SUCCESS } from 'utils/constants/queryStatuses';
import { getTotalArticles } from './api';
import { arrangeArticles, LABELS_DATA } from './utils';

const ARTICLES_STORE_KEY = 'articles';
const OFFSET_STEP = 4;

const fetchArticles = (condition) => async (dispatch, getState) => {
  try {
    dispatch(updateKeyValue(ARTICLES_STORE_KEY, { status: PENDING, data: { condition } }));
    const { keyValueStore: { [ARTICLES_STORE_KEY]: { data: { condition: targetCondition } = {} } = {} } } = getState();
    if (condition !== targetCondition) {
      // handle race condition
      return;
    }

    const articles = await getTotalArticles(condition);
    dispatch(setKeyValue(
      ARTICLES_STORE_KEY,
      { status: SUCCESS, data: { condition, articles: arrangeArticles(articles) } },
    ));
  } catch {
    dispatch(setKeyValue(ARTICLES_STORE_KEY, { status: FAILURE }));
  }
};

export const useFetchArticles = (condition, defaultOffset) => {
  const dispatch = useDispatch();
  const [
    { status: articlesFetchingStatus, data: { condition: articlesCondition, articles: totalArticles } = {} } = {},
  ] = useKeyValueStore(ARTICLES_STORE_KEY);

  const [offset, setOffset] = useState(defaultOffset ?? OFFSET_STEP);

  useEffect(() => {
    if (articlesCondition === condition && [PENDING, SUCCESS, FAILURE].includes(articlesFetchingStatus)) {
      return;
    }
    dispatch(fetchArticles(condition));
  }, [articlesCondition, articlesFetchingStatus, condition, dispatch]);

  const isLoaded = articlesCondition === condition && articlesFetchingStatus === SUCCESS;

  const articles = useMemo(() => (totalArticles ?? []).slice(0, offset), [offset, totalArticles]);

  return {
    articles,
    labels: LABELS_DATA,
    hasMore: isLoaded && articles.length < totalArticles.length,
    loadMoreArticles: useCallback((more = OFFSET_STEP) => {
      setOffset((prevOffset) => prevOffset + more);
    }, []),
    articlesFetchingStatus,
    isLoaded,
  };
};
